import unserialize from 'locutus/php/var/unserialize'
import { hostDateToString, todayString, dateStringAddDays, daysBetweenDateStrings } from '../../views/dashboard/util'
import Item from '../../views/dashboard/tracker/models/item'

// The macroData component of user has this structure.
const mdInitial = {
  // data saved from FETCH_USER_FULFILLED
  legacyMovingTargets: null,
  legacyMovingTargetStartDate: null,

  // {'YYYY-MM-DD': {level: 0}, ...}, if data for weigh-in on YYYY-MM-DD not requested yet
  // {'YYYY-MM-DD': {level: 1}, ...}, if data for weigh-in on YYYY-MM-DD requested but not available yet
  // {'YYYY-MM-DD': {level: 2, source: {...}, result: {...}}, ...}, if data for weigh-in on YYYY-MM-DD available
  weighInDetails: {},

  // results for today, calculated at FETCH_USER_FULFILLED; macro targets are stage 5
  movingTargets: null,
  part4Invoked: null,
  part4FatPerMyriad: null,
  isHighDay: null,
  protein: null,
  carbs: null,
  fat: null,

  // flags about the interval from the latest weigh-in up to but not including today
  anyHighLowDays: null,
  anyMovingTarget: null,

  // average tracked macros from latest weigh-in up to but not including today
  avgProtein: null,
  avgCarbs: null,
  avgFat: null,
  avgFiber: null,

  // average stage 4 targets from latest weigh-in up to but not including today
  avgTargetProtein: null,
  avgTargetCarbs: null,
  avgTargetFat: null
}

// Calculate macros from the given weigh-in source data.
function calculateMacros(md, source) {
  const movingTargetsDayLimit = source.dayCount + 1
  let safeDayCount = (source.high_low_enabled && movingTargetsDayLimit < 8) ? 8 : movingTargetsDayLimit
  const part4DayLimit = source.dayCount + 7
  const weighInDateString = hostDateToString(source.report_date)
  let anyHighLowDays = false
  let anyMovingTargets = false

  // Build an array of macro adjustments.
  let macroAdjustments = Array(part4DayLimit).fill(null)
  source.macroAdjustments.forEach((ma) => {
    const adjustmentDateOffset = daysBetweenDateStrings(weighInDateString, hostDateToString(ma.adjustment_date))
    if(adjustmentDateOffset >= 0 && adjustmentDateOffset < part4DayLimit) {
      if(adjustmentDateOffset < movingTargetsDayLimit) {
        macroAdjustments[adjustmentDateOffset] = ma
      } else {
        macroAdjustments[adjustmentDateOffset] = {
          adjustment_date: ma.adjustment_date,
          moving_targets: 0,
          part_4_invoked: ma.part_4_invoked,
          part_4_fat_per_myriad: ma.part_4_fat_per_myriad,
          calorie_boost: ma.calorie_boost,
          tdee_weight: ma.tdee_weight,
          days_until_check_in: ma.days_until_check_in
        }
        if(adjustmentDateOffset + 1 > safeDayCount) {
          safeDayCount = part4DayLimit < adjustmentDateOffset + 2 ? part4DayLimit : adjustmentDateOffset + 2
        }
      }
    }
  })

  // ***** Stage 0 *****
  const stage_0_day_count = (safeDayCount < 7) ? 7 : safeDayCount
  let baseProtein = source.protein
  let baseCarbs = source.carbs
  let baseFat = source.fat
  let stage_0_protein = Array(stage_0_day_count).fill(Math.round(source.protein))
  let stage_0_carbs = Array(stage_0_day_count).fill(Math.round(source.carbs))
  let stage_0_fat = Array(stage_0_day_count).fill(Math.round(source.fat))
  let macroChangeDay = Array(7).fill(0)

  source.macroHistory.forEach((mh) => {
    const startDateOffset = daysBetweenDateStrings(weighInDateString, hostDateToString(mh.start_date))
    if(startDateOffset >= 0 && startDateOffset < stage_0_day_count) {
      const endDateOffset = daysBetweenDateStrings(weighInDateString, hostDateToString(mh.end_date))
      const fillEnd = (endDateOffset < stage_0_day_count) ? endDateOffset : stage_0_day_count
      if(endDateOffset < 7) {
        macroChangeDay[endDateOffset] = 1
      }
      stage_0_protein.fill(mh.protein, startDateOffset, fillEnd)
      stage_0_carbs.fill(mh.carbs, startDateOffset, fillEnd)
      stage_0_fat.fill(mh.fat, startDateOffset, fillEnd)
    }
  })

  source.settingsMacros.forEach((sm) => {
    baseProtein = sm.protein
    baseCarbs = sm.carbs
    baseFat = sm.fat
    const startDateOffset = daysBetweenDateStrings(weighInDateString, hostDateToString(sm.date))
    if(startDateOffset >= 0 && startDateOffset < stage_0_day_count) {
      if (startDateOffset < 7) {
        macroChangeDay[startDateOffset] = 1
      }
      stage_0_protein.fill(Math.round(sm.protein), startDateOffset, stage_0_day_count)
      stage_0_carbs.fill(Math.round(sm.carbs), startDateOffset, stage_0_day_count)
      stage_0_fat.fill(Math.round(sm.fat), startDateOffset, stage_0_day_count)
    }
  })

  // ***** Stage 1 *****
  let stage_1_protein = stage_0_protein.slice()
  let stage_1_carbs = stage_0_carbs.slice()
  let stage_1_fat = stage_0_fat.slice()
  let highLowMask = 0
  if(source.high_low_enabled) {
    const carbsToFat = 15
    let maxCarbsIncrease
    let maxCarbsDecrease
    let totalCarbsBeforeDay = Array(7).fill(0)
    let highLowTweaks = {}
    if(source.high_low_tweaks) {
      highLowTweaks = unserialize(source.high_low_tweaks)
    }
    let highLowFactor
    let totalBaseCarbs = 0
    let totalCarbsBeforeToday
    let averageRemainingCarbs
    let trainingCount
    let restingCount
    let m
    let n
    let offsetMaxCarbsIncrease
    let offsetMaxCarbsDecrease
    let offsetTrainingCarbsIncrease
    let offsetRestingCarbsDecrease
    let restingFatDecrease
    let trainingFatIncrease
    let restingCarbsDecrease
    let trainingCarbsIncrease
    let finalCarbs
    let finalFat
    let adjustmentNeeded
    let oldHighLowDayCount
    let highLowDayCount = 7

    for(let k = 0; k < 7; ++k) {
      oldHighLowDayCount = highLowDayCount
      if(macroAdjustments[k] !== null) {
        if(macroAdjustments[k].days_until_check_in !== null) {
          highLowDayCount = k + macroAdjustments[k].days_until_check_in
          if(highLowDayCount < 5 || highLowDayCount > 7) {
            highLowDayCount = 7
          }
        }
      }
      const roundedProtein = stage_0_protein[k]
      const roundedCarbs = stage_0_carbs[k]
      const roundedFat = stage_0_fat[k]

      if(roundedCarbs < 150) {
        maxCarbsIncrease = roundedCarbs * 0.700
      } else if(roundedCarbs < 200) {
        maxCarbsIncrease = roundedCarbs * 0.675
      } else if(roundedCarbs < 250) {
        maxCarbsIncrease = roundedCarbs * 0.650
      } else if(roundedCarbs < 300) {
        maxCarbsIncrease = roundedCarbs * 0.625
      } else if(roundedCarbs < 350) {
        maxCarbsIncrease = roundedCarbs * 0.600
      } else if(roundedCarbs < 400) {
        maxCarbsIncrease = roundedCarbs * 0.575
      } else if(roundedCarbs < 450) {
        maxCarbsIncrease = roundedCarbs * 0.550
      } else if(roundedCarbs < 500) {
        maxCarbsIncrease = roundedCarbs * 0.525
      } else if(roundedCarbs < 550) {
        maxCarbsIncrease = roundedCarbs * 0.500
      } else if(roundedCarbs < 600) {
        maxCarbsIncrease = roundedCarbs * 0.475
      } else if(roundedCarbs < 650) {
        maxCarbsIncrease = roundedCarbs * 0.450
      } else if(roundedCarbs < 700) {
        maxCarbsIncrease = roundedCarbs * 0.425
      } else if(roundedCarbs < 750) {
        maxCarbsIncrease = roundedCarbs * 0.400
      } else {
        maxCarbsIncrease = roundedCarbs * 0.375
      }

      if(roundedCarbs > roundedFat * carbsToFat) {
        maxCarbsDecrease = roundedFat * carbsToFat * 0.20
      } else {
        maxCarbsDecrease = roundedCarbs * 0.20
      }

      adjustmentNeeded = false;
      if(k === 0) {
        highLowMask = source.high_low_mask
        highLowFactor = source.high_low_factor
        totalCarbsBeforeToday = 0
        averageRemainingCarbs = roundedCarbs
        adjustmentNeeded = true
      } else if(k >= highLowDayCount) {
        restingFatDecrease = 0
        trainingFatIncrease = 0
        restingCarbsDecrease = 0
        trainingCarbsIncrease = 0
      } else if(k in highLowTweaks) {
        highLowMask = highLowTweaks[k][0]
        highLowFactor = highLowTweaks[k][1]
        totalCarbsBeforeToday = totalCarbsBeforeDay[k]
        averageRemainingCarbs = (totalBaseCarbs + roundedCarbs * (highLowDayCount - k) - totalCarbsBeforeToday) / (highLowDayCount - k)
        adjustmentNeeded = true
      } else if(macroChangeDay[k] || highLowDayCount !== oldHighLowDayCount) {
        totalCarbsBeforeToday = totalCarbsBeforeDay[k]
        averageRemainingCarbs = (totalBaseCarbs + roundedCarbs * (highLowDayCount - k) - totalCarbsBeforeToday) / (highLowDayCount - k)
        adjustmentNeeded = true
      }

      if(adjustmentNeeded) {
        const allowedMask = (1 << highLowDayCount) - 1
        const effectiveMask = highLowMask & allowedMask
        if(effectiveMask !== 0 && effectiveMask !== allowedMask) {
          anyHighLowDays = true
        }
        trainingCount = 0
        restingCount = 0
        m = 1 << k
        for(n = k; n < highLowDayCount; ++n) {
          if(highLowMask & m) {
            ++trainingCount
          } else {
            ++restingCount
          }
          m <<= 1
        }

        offsetMaxCarbsIncrease = maxCarbsIncrease + roundedCarbs - averageRemainingCarbs
        offsetMaxCarbsDecrease = maxCarbsDecrease - roundedCarbs + averageRemainingCarbs
        if(offsetMaxCarbsIncrease < 0)
          offsetMaxCarbsIncrease = 0
        if(offsetMaxCarbsDecrease < 0)
          offsetMaxCarbsDecrease = 0

        if(trainingCount === 0 || restingCount === 0) {
          offsetTrainingCarbsIncrease = 0
          offsetRestingCarbsDecrease = 0
        } else if(offsetMaxCarbsIncrease * trainingCount > offsetMaxCarbsDecrease * restingCount) {
          offsetRestingCarbsDecrease = offsetMaxCarbsDecrease * highLowFactor / 100
          offsetTrainingCarbsIncrease = offsetRestingCarbsDecrease * restingCount / trainingCount
        } else {
          offsetTrainingCarbsIncrease = offsetMaxCarbsIncrease * highLowFactor / 100
          offsetRestingCarbsDecrease = offsetTrainingCarbsIncrease * trainingCount / restingCount
        }

        restingFatDecrease = Math.round((offsetRestingCarbsDecrease + roundedCarbs - averageRemainingCarbs) / carbsToFat)
        trainingFatIncrease = Math.round((offsetTrainingCarbsIncrease - roundedCarbs + averageRemainingCarbs) / carbsToFat)
        restingCarbsDecrease = Math.round(offsetRestingCarbsDecrease + roundedCarbs - averageRemainingCarbs)
        trainingCarbsIncrease = Math.round(offsetTrainingCarbsIncrease - roundedCarbs + averageRemainingCarbs)

        m = 1 << k
        for(n = k; n < 7; ++n) {
          totalCarbsBeforeDay[n] = totalCarbsBeforeToday
          if(highLowMask & m) {
            totalCarbsBeforeToday += roundedCarbs + trainingCarbsIncrease
          } else {
            totalCarbsBeforeToday += roundedCarbs - restingCarbsDecrease
          }
          m <<= 1
        }
      }

      if(highLowMask & (1 << k)) {
        finalCarbs = roundedCarbs + trainingCarbsIncrease
        finalFat = roundedFat + trainingFatIncrease
      } else {
        finalCarbs = roundedCarbs - restingCarbsDecrease
        finalFat = roundedFat - restingFatDecrease
      }

      stage_1_carbs[k] = finalCarbs
      stage_1_fat[k] = finalFat
      totalBaseCarbs += roundedCarbs
    }
  }

  // Incorporate the legacy moving targets information.
  if(md.legacyMovingTargets) {
    const movingTargetStartDateOffset = daysBetweenDateStrings(weighInDateString, hostDateToString(md.legacyMovingTargetStartDate))
    if(movingTargetStartDateOffset >= 0 && movingTargetStartDateOffset < movingTargetsDayLimit) {
      if(macroAdjustments[movingTargetStartDateOffset] === null) {
        macroAdjustments[movingTargetStartDateOffset] = {
          adjustment_date: md.legacyMovingTargetStartDate,
          moving_targets: 1,
          part_4_invoked: 0,
          part_4_fat_per_myriad: 0,
          calorie_boost: null,
          tdee_weight: null,
          days_until_check_in: null
        }
      }
    }
  }

  // Build an array of tracked totals.
  let trackedTotals = Array(safeDayCount).fill(null)
  for(let key in source.trackedTotals) {
    const keyDateOffset = daysBetweenDateStrings(weighInDateString, key)
    if(keyDateOffset >= 0 && keyDateOffset < safeDayCount) {
      trackedTotals[keyDateOffset] = source.trackedTotals[key]
    }
  }

  // Stages 2 through 5 are calculated day by day.
  let stage_2_protein = Array(safeDayCount)
  let stage_2_carbs = Array(safeDayCount)
  let stage_2_fat = Array(safeDayCount)
  let stage_3_protein = Array(safeDayCount)
  let stage_3_carbs = Array(safeDayCount)
  let stage_3_fat = Array(safeDayCount)
  let stage_4_protein = Array(safeDayCount)
  let stage_4_carbs = Array(safeDayCount)
  let stage_4_fat = Array(safeDayCount)
  let stage_5_protein = Array(safeDayCount)
  let stage_5_carbs = Array(safeDayCount)
  let stage_5_fat = Array(safeDayCount)
  let stage_4_sum_protein = Array(safeDayCount)
  let stage_4_sum_carbs = Array(safeDayCount)
  let stage_4_sum_fat = Array(safeDayCount)
  let tracked_sum_protein = Array(safeDayCount)
  let tracked_sum_carbs = Array(safeDayCount)
  let tracked_sum_fat = Array(safeDayCount)
  let tracked_sum_fiber = Array(safeDayCount)
  let movingTargets = source.earlierMovingTargets
  let cumulative_stage_4_protein = 0
  let cumulative_stage_4_carbs = 0
  let cumulative_stage_4_fat = 0
  let cumulative_tracked_protein = 0
  let cumulative_tracked_carbs = 0
  let cumulative_tracked_fat = 0
  let cumulative_tracked_fiber = 0
  let finalMovingTargets = 0
  let finalPart4Invoked = 0
  let finalPart4FatPerMyriad = 0
  for(let k = 0; k < safeDayCount; ++k) {
    let part4Invoked = 0
    let part4FatPerMyriad = 0
    if(macroAdjustments[k] !== null) {
      movingTargets = macroAdjustments[k].moving_targets
      part4Invoked = macroAdjustments[k].part_4_invoked
      part4FatPerMyriad = macroAdjustments[k].part_4_fat_per_myriad
    }
    if(k === source.dayCount - 1) {
      finalMovingTargets = movingTargets
      finalPart4Invoked = part4Invoked
      finalPart4FatPerMyriad = part4FatPerMyriad
    }
    const stage_1_calories = (stage_1_protein[k] + stage_1_carbs[k]) * 4 + stage_1_fat[k] * 9

    // ***** Stage 2 *****
    stage_2_protein[k] = stage_1_protein[k]
    stage_2_carbs[k] = stage_1_carbs[k]
    stage_2_fat[k] = stage_1_fat[k]
    if(movingTargets && k !== 0 && k <= source.dayCount) {
      if(k < source.dayCount - 1) {
        anyMovingTargets = true
      }
      let minCarbLimit = 20
      let maxCarbLimit = 1031.25
      let minFatLimit = (stage_1_calories * .15) / 9
      let maxFatLimit = (stage_1_calories * .45) / 9
      let targetCarbs = stage_1_carbs[k]

      if(targetCarbs < 150)
        minCarbLimit = targetCarbs * .30
      else if(targetCarbs >= 150 && targetCarbs < 750)
        minCarbLimit = targetCarbs * ((targetCarbs * 0.000541666668) + 0.21875)
      else if(targetCarbs >= 750)
        minCarbLimit = targetCarbs * .625

      if(targetCarbs < 150)
        maxCarbLimit = targetCarbs * 1.70
      else if(targetCarbs >= 150 && targetCarbs < 750)
        maxCarbLimit = targetCarbs * ((targetCarbs * -0.000541666668) + 1.781250005)
      else if(targetCarbs >= 750)
        maxCarbLimit = targetCarbs * 1.375

      let recommendedTargetCarbs
      let recommendedTargetFat
      if (movingTargets === 1 || k >= 7) {
        // standard option, or a week has already passed
        recommendedTargetCarbs = stage_1_carbs[k] + cumulative_stage_4_carbs - cumulative_tracked_carbs
        recommendedTargetFat = stage_1_fat[k] + cumulative_stage_4_fat - cumulative_tracked_fat
      } else {
        // dispersed option
        recommendedTargetCarbs = stage_1_carbs[k] + (cumulative_stage_4_carbs - cumulative_tracked_carbs) / (7 - k)
        recommendedTargetFat = stage_1_fat[k] + (cumulative_stage_4_fat - cumulative_tracked_fat) / (7 - k)
      }

      if(recommendedTargetCarbs < minCarbLimit) {
        recommendedTargetCarbs = minCarbLimit
      }
      else if(recommendedTargetCarbs > maxCarbLimit) {
        recommendedTargetCarbs = maxCarbLimit
      }

      if(recommendedTargetFat < minFatLimit) {
        recommendedTargetFat = minFatLimit
      }
      else if(recommendedTargetFat > maxFatLimit) {
        recommendedTargetFat = maxFatLimit
      }

      stage_2_carbs[k] = recommendedTargetCarbs
      stage_2_fat[k] = recommendedTargetFat
    }

    // ***** Stage 3 *****
    stage_3_protein[k] = stage_2_protein[k]
    stage_3_carbs[k] = stage_2_carbs[k]
    stage_3_fat[k] = stage_2_fat[k]
    if(movingTargets && part4Invoked) {
      const stage_2_calories = (stage_2_protein[k] + stage_2_carbs[k]) * 4 + stage_2_fat[k] * 9
      const fatStage3 = (part4FatPerMyriad / 10000) * (stage_2_calories / 9)
      const carbsStage3 = (stage_2_calories - (stage_2_protein[k] * 4 + fatStage3 * 9)) / 4
      stage_3_carbs[k] = carbsStage3
      stage_3_fat[k] = fatStage3
    }

    // ***** Stage 4 *****
    stage_4_protein[k] = stage_1_protein[k]
    stage_4_carbs[k] = stage_1_carbs[k]
    stage_4_fat[k] = stage_1_fat[k]
    if(!part4Invoked) {
      // nothing more to do
    } else if(movingTargets) {
      stage_4_carbs[k] += stage_3_carbs[k] - stage_2_carbs[k]
      stage_4_fat[k] += stage_3_fat[k] - stage_2_fat[k]
    } else {
      const fatStage4 = (part4FatPerMyriad / 10000) * (stage_1_calories / 9)
      const carbsStage4 = (stage_1_calories - (stage_1_protein[k] * 4 + fatStage4 * 9)) / 4
      stage_4_carbs[k] = carbsStage4
      stage_4_fat[k] = fatStage4
    }

    // ***** Stage 5 *****
    if(movingTargets) {
      stage_5_protein[k] = stage_3_protein[k]
      stage_5_carbs[k] = stage_3_carbs[k]
      stage_5_fat[k] = stage_3_fat[k]
    } else {
      stage_5_protein[k] = stage_4_protein[k]
      stage_5_carbs[k] = stage_4_carbs[k]
      stage_5_fat[k] = stage_4_fat[k]
    }

    // ***** Stage 4 Sum (through previous day) *****
    stage_4_sum_protein[k] = cumulative_stage_4_protein
    stage_4_sum_carbs[k] = cumulative_stage_4_carbs
    stage_4_sum_fat[k] = cumulative_stage_4_fat

    // ***** Tracked Sum (through previous day) *****
    tracked_sum_protein[k] = cumulative_tracked_protein
    tracked_sum_carbs[k] = cumulative_tracked_carbs
    tracked_sum_fat[k] = cumulative_tracked_fat
    tracked_sum_fiber[k] = cumulative_tracked_fiber

    cumulative_stage_4_protein += stage_4_protein[k]
    cumulative_stage_4_carbs += stage_4_carbs[k]
    cumulative_stage_4_fat += stage_4_fat[k]
    if(trackedTotals[k] !== null) {
      cumulative_tracked_protein += trackedTotals[k].protein
      cumulative_tracked_carbs += trackedTotals[k].carbs
      cumulative_tracked_fat += trackedTotals[k].fat
      cumulative_tracked_fiber += trackedTotals[k].fiber
    }
  }

  // Build final results.
  let stage_0 = {}
  let stage_1 = {}
  let stage_2 = {}
  let stage_3 = {}
  let stage_4 = {}
  let stage_5 = {}
  let stage_4_sum = {}
  let tracked_sum = {}
  for(let k = 0; k < safeDayCount; ++k) {
    const dateString = dateStringAddDays(weighInDateString, k)
    stage_0[dateString] = {protein: stage_0_protein[k], carbs: stage_0_carbs[k], fat: stage_0_fat[k]}
    stage_1[dateString] = {protein: stage_1_protein[k], carbs: stage_1_carbs[k], fat: stage_1_fat[k]}
    stage_2[dateString] = {protein: stage_2_protein[k], carbs: stage_2_carbs[k], fat: stage_2_fat[k]}
    stage_3[dateString] = {protein: stage_3_protein[k], carbs: stage_3_carbs[k], fat: stage_3_fat[k]}
    stage_4[dateString] = {protein: stage_4_protein[k], carbs: stage_4_carbs[k], fat: stage_4_fat[k]}
    stage_5[dateString] = {protein: stage_5_protein[k], carbs: stage_5_carbs[k], fat: stage_5_fat[k]}
    stage_4_sum[dateString] = {protein: stage_4_sum_protein[k], carbs: stage_4_sum_carbs[k], fat: stage_4_sum_fat[k]}
    tracked_sum[dateString] = {protein: tracked_sum_protein[k], carbs: tracked_sum_carbs[k], fat: tracked_sum_fat[k], fiber: tracked_sum_fiber[k]}
  }

  return {
    stage_0: stage_0,
    stage_1: stage_1,
    stage_2: stage_2,
    stage_3: stage_3,
    stage_4: stage_4,
    stage_5: stage_5,
    stage_4_sum: stage_4_sum,
    tracked_sum: tracked_sum,
    movingTargets: finalMovingTargets,
    part4Invoked: finalPart4Invoked,
    part4FatPerMyriad: finalPart4FatPerMyriad,
    isHighDay: (source.high_low_enabled && source.dayCount < 8 && (highLowMask & (1 << (source.dayCount - 1)))) ? true : false,
    baseProtein: baseProtein,
    baseCarbs: baseCarbs,
    baseFat: baseFat,
    protein: stage_5_protein[source.dayCount - 1],
    carbs: stage_5_carbs[source.dayCount - 1],
    fat: stage_5_fat[source.dayCount - 1],
    anyHighLowDays: anyHighLowDays,
    anyMovingTargets: anyMovingTargets,
    avgProtein: (source.trackedDayCount === 0) ? 0 : (tracked_sum_protein[source.trackedDayCount] / source.trackedDayCount),
    avgCarbs: (source.trackedDayCount === 0) ? 0 : (tracked_sum_carbs[source.trackedDayCount] / source.trackedDayCount),
    avgFat: (source.trackedDayCount === 0) ? 0 : (tracked_sum_fat[source.trackedDayCount] / source.trackedDayCount),
    avgFiber: (source.trackedDayCount === 0) ? 0 : (tracked_sum_fiber[source.trackedDayCount] / source.trackedDayCount),
    avgTargetProtein: (source.trackedDayCount === 0) ? 0 : (stage_4_sum_protein[source.trackedDayCount] / source.trackedDayCount),
    avgTargetCarbs: (source.trackedDayCount === 0) ? 0 : (stage_4_sum_carbs[source.trackedDayCount] / source.trackedDayCount),
    avgTargetFat: (source.trackedDayCount === 0) ? 0 : (stage_4_sum_fat[source.trackedDayCount] / source.trackedDayCount)
  }
}

// Update macroData based on FETCH_USER_FULFILLED action.
function mdFetchUserFulfilled(md, payload) {
  const weighInDateString = hostDateToString(payload.lastWeighInDate)
  const trackedDayCount = daysBetweenDateStrings(weighInDateString, todayString())

  // For each weigh-in, create placeholder entry if no entry exists.
  let widNew = Object.assign({}, md.weighInDetails)
  payload.checkin_dates.forEach((reportDateString) => {
    if(!(reportDateString in widNew)) {
      widNew[reportDateString] = {level: 0}
    }
  })

  // Start building the new macroData.
  let mdNew = {
    legacyMovingTargets: payload.moving_targets,
    legacyMovingTargetStartDate: payload.moving_target_start_date,
    weighInDetails: widNew,
    // Data from here on will be filled in later.
    movingTargets: md.movingTargets,
    protein: md.protein,
    carbs: md.carbs,
    fat: md.fat
  }

  // Calculate macros for the latest weigh-in.
  const source = {
    report_date: payload.lastWeighInDate,
    calories: payload.calories,
    protein: payload.protein,
    carbs: payload.carbs,
    fat: payload.fat,
    tdee: payload.tdee,
    macroHistory: payload.macroHistory,
    high_low_enabled: payload.high_low_enabled,
    high_low_mask: payload.high_low_mask,
    high_low_factor: payload.high_low_factor,
    high_low_tweaks: payload.high_low_tweaks,
    earlierMovingTargets: payload.earlierMovingTargets,
    macroAdjustments: payload.macroAdjustments,
    settingsMacros: payload.settingsMacros,
    trackedTotals: payload.trackedTotals,
    trackedDayCount: trackedDayCount,
    dayCount: trackedDayCount + 1
  }
  const result = calculateMacros(mdNew, source)
  mdNew.weighInDetails[weighInDateString] = {level: 2, source: source, result: result}
  mdNew.movingTargets = result.movingTargets
  mdNew.part4Invoked = result.part4Invoked
  mdNew.part4FatPerMyriad = result.part4FatPerMyriad
  mdNew.isHighDay = result.isHighDay
  mdNew.baseProtein = result.baseProtein
  mdNew.baseCarbs = result.baseCarbs
  mdNew.baseFat = result.baseFat
  mdNew.protein = result.protein
  mdNew.carbs = result.carbs
  mdNew.fat = result.fat
  mdNew.anyHighLowDays = result.anyHighLowDays
  mdNew.anyMovingTargets = result.anyMovingTargets
  mdNew.avgProtein = result.avgProtein
  mdNew.avgCarbs = result.avgCarbs
  mdNew.avgFat = result.avgFat
  mdNew.avgFiber = result.avgFiber
  mdNew.avgTargetProtein = result.avgTargetProtein
  mdNew.avgTargetCarbs = result.avgTargetCarbs
  mdNew.avgTargetFat = result.avgTargetFat
  return mdNew
}

// Update macroData based on FETCH_WEIGH_IN_DETAILS action.
function mdFetchWeighInDetails(md, dateString) {
  let widNew = md.weighInDetails
  widNew[dateString] = {level: 1}
  return Object.assign({}, md, {weighInDetails: widNew})
}

// Update macroData based on FETCH_WEIGH_IN_DETAILS_FULFILLED action.
function mdFetchWeighInDetailsFulfilled(md, payload) {
  const weighInDateString = hostDateToString(payload.details.report_date)

  // Find the next weigh-in after this one, if any.
  let nextWeighInDateString = null
  for(let key in md.weighInDetails) {
    if(key > weighInDateString && (nextWeighInDateString === null || key < nextWeighInDateString)) {
      nextWeighInDateString = key
    }
  }

  // Get the number of days for which macros should be calculated.
  let trackedDayCount
  let dayCount
  if(nextWeighInDateString === null) {
    trackedDayCount = daysBetweenDateStrings(weighInDateString, todayString())
    dayCount = trackedDayCount + 1
  } else {
    trackedDayCount = daysBetweenDateStrings(weighInDateString, nextWeighInDateString)
    dayCount = trackedDayCount
  }

  // Calculate macros for this weigh-in.
  const source = Object.assign({}, payload.details, {trackedDayCount: trackedDayCount, dayCount: dayCount})
  const result = calculateMacros(md, source)
  let widNew = Object.assign({}, md.weighInDetails)
  widNew[weighInDateString] = {level: 2, source: source, result: result}
  const mdNew = {
    legacyMovingTargets: md.legacyMovingTargets,
    legacyMovingTargetStartDate: md.legacyMovingTargetStartDate,
    weighInDetails: widNew,
    movingTargets: (nextWeighInDateString === null) ? result.movingTargets : md.movingTargets,
    part4Invoked: (nextWeighInDateString === null) ? result.part4Invoked : md.part4Invoked,
    part4FatPerMyriad: (nextWeighInDateString === null) ? result.part4FatPerMyriad : md.part4FatPerMyriad,
    isHighDay: (nextWeighInDateString === null) ? result.isHighDay : md.isHighDay,
    baseProtein: (nextWeighInDateString === null) ? result.baseProtein : md.baseProtein,
    baseCarbs: (nextWeighInDateString === null) ? result.baseCarbs : md.baseCarbs,
    baseFat: (nextWeighInDateString === null) ? result.baseFat : md.baseFat,
    protein: (nextWeighInDateString === null) ? result.protein : md.protein,
    carbs: (nextWeighInDateString === null) ? result.carbs : md.carbs,
    fat: (nextWeighInDateString === null) ? result.fat : md.fat,
    anyHighLowDays: (nextWeighInDateString === null) ? result.anyHighLowDays : md.anyHighLowDays,
    anyMovingTargets: (nextWeighInDateString === null) ? result.anyMovingTargets : md.anyMovingTargets,
    avgProtein: (nextWeighInDateString === null) ? result.avgProtein : md.avgProtein,
    avgCarbs: (nextWeighInDateString === null) ? result.avgCarbs : md.avgCarbs,
    avgFat: (nextWeighInDateString === null) ? result.avgFat : md.avgFat,
    avgFiber: (nextWeighInDateString === null) ? result.avgFiber : md.avgFiber,
    avgTargetProtein: (nextWeighInDateString === null) ? result.avgTargetProtein : md.avgTargetProtein,
    avgTargetCarbs: (nextWeighInDateString === null) ? result.avgTargetCarbs : md.avgTargetCarbs,
    avgTargetFat: (nextWeighInDateString === null) ? result.avgTargetFat : md.avgTargetFat,
  }
  return mdNew
}

// Update macroData based on a timeline update.
function mdTimelineUpdate(md, affectedDates, timeline) {
  let widNew = Object.assign({}, md.weighInDetails)
  let mdNew = Object.assign({}, md)

  // Update each weigh-in as necessary.
  for(let weighInDateString in md.weighInDetails) {
    if(md.weighInDetails[weighInDateString].level === 2) {
      // Find the next weigh-in after this one, if any.
      let nextWeighInDateString = null
      for(let key in md.weighInDetails) {
        if(key > weighInDateString && (nextWeighInDateString === null || key < nextWeighInDateString)) {
          nextWeighInDateString = key
        }
      }

      // Make any updates to the tracked totals.
      let newTrackedTotals = Object.assign({}, md.weighInDetails[weighInDateString].source.trackedTotals)
      let anyUpdate = false
      affectedDates.forEach((affectedDate) => {
        if(affectedDate >= weighInDateString && (nextWeighInDateString === null || affectedDate < nextWeighInDateString)) {
          let totalProtein = 0
          let totalCarbs = 0
          let totalFat = 0
          let totalFiber = 0
          timeline[affectedDate].forEach((data) => {
            const item = new Item(data)
            if(item.itemType === 'recipe') {
              totalProtein += item.getRecipeProtein()
              totalCarbs += item.getRecipeCarbs()
              totalFat += item.getRecipeFat()
              totalFiber += item.getRecipeFiber()
            } else {
              totalProtein += item.getProtein()
              totalCarbs += item.getCarbs()
              totalFat += item.getFat()
              totalFiber += item.getFiber()
            }
          })
          newTrackedTotals[affectedDate] = {
            protein: totalProtein,
            carbs: totalCarbs,
            fat: totalFat,
            fiber: totalFiber,
          }
          anyUpdate = true
        }
      })

      // Recalculate macros if there was any update.
      if(anyUpdate) {
        const source = Object.assign({}, md.weighInDetails[weighInDateString].source, {trackedTotals: newTrackedTotals})
        const result = calculateMacros(md, source)
        widNew[weighInDateString] = {level: 2, source: source, result: result}
        if(nextWeighInDateString === null) {
          mdNew.movingTargets = result.movingTargets
          mdNew.part4Invoked = result.part4Invoked
          mdNew.part4FatPerMyriad = result.part4FatPerMyriad
          mdNew.isHighDay = result.isHighDay
          mdNew.protein = result.protein
          mdNew.carbs = result.carbs
          mdNew.fat = result.fat
          mdNew.anyHighLowDays = result.anyHighLowDays
          mdNew.anyMovingTargets = result.anyMovingTargets
          mdNew.avgProtein = result.avgProtein
          mdNew.avgCarbs = result.avgCarbs
          mdNew.avgFat = result.avgFat
          mdNew.avgFiber = result.avgFiber
          mdNew.avgTargetProtein = result.avgTargetProtein
          mdNew.avgTargetCarbs = result.avgTargetCarbs
          mdNew.avgTargetFat = result.avgTargetFat
        }
      }
    }
  }

  mdNew.weighInDetails = widNew
  return mdNew
}

export { mdInitial, mdFetchUserFulfilled, mdFetchWeighInDetails, mdFetchWeighInDetailsFulfilled, mdTimelineUpdate }